import * as util from "./util.js";
import * as vectormath from "./vectormath.js";
import * as cconst from "./const.js";
import report from "./report.js";
const Class = util.Class;
const TWOPI = Math.PI * 2.0;
const FFX = 0;
const FFY = 1;
const FTOT = 2;
const calc_fxfy_ret = new util.cachering(function () {
  return [0, 0];
}, 64);
const _sin_table = (function () {
  const steps = 32768;
  const table = new Float64Array(steps);
  let t = 0;
  const dt = (2 * Math.PI) / (steps - 1);

  console.log("building sin table approximation. . .");

  for (var i = 0; i < steps; i++, t += dt) {
    table[i] = Math.sin(t);
  }

  const TWOPI = Math.PI * 2.0;
  const ONETWOPI = 1.0 / TWOPI;
  const PIOVER2 = Math.PI / 2.0;

  return {
    sin: function (s: number) {
      i = ~~(s * 0.15915494309189535 * 32768);
      i = i & 32767;
      //i = i < 0 ? i + 32768 : i;

      return table[i];
    },

    cos: function (s: number) {
      s += 1.5707963267948966; //PIOVER2;
      i = ~~(s * 0.15915494309189535 * 32768);
      i = i & 32767;
      //i = i < 0 ? i + 32768 : i;

      return table[i];
    },

    test: function () {
      for (let i = -32; i < 32; i++) {
        console.log(Math.sin(i * 0.2).toFixed(4), this.sin(i * 0.2).toFixed(4));
      }
    },
  };
})();

/*
  var _sin_table = window._sin_table = {
    cos1 : function(n) {
      if (isNaN(n)) {
        throw new Error("NaN!");
      }
      
      function wave(f) {
        f = Math.tent(f);
        return f;
      }
      
      var f = (1.0 - wave(n/(Math.PI*2.0)))
      //f = f*f*(3.0-2.0*f);
      
      return f*2.0 - 1.0;
    },
    
    cos : function(n) {
      return this.cos1(n);
    },
    
    sin : function(n) {
      return this.cos1(n+Math.PI*0.5);
    },
    
    test : function() {
    }
  }
  //*/

class FFT {
  size: number;
  radial: { average: Float64Array; totals: Float64Array } | undefined;
  fft: Float64Array;
  rand: util.MersenneRandom;
  fft_pointcachefx: util.hashtable<number>;
  fft_pointcachefy: util.hashtable<number>;
  fmin: number;
  fmax: number;
  jitter: number;
  jittab: number[];
  totpoint: number;

  constructor(dimen: number) {
    this.size = dimen;
    this.radial = undefined;
    this.fft = new Float64Array(dimen * dimen * FTOT);

    this.rand = new util.MersenneRandom();

    this.fft_pointcachefx = new util.hashtable<number>();
    this.fft_pointcachefy = new util.hashtable<number>();
    //    this.fft_pointcachefmin = new util.hashtable();
    //      this.fft_pointcachefmax = new util.hashtable();

    (this.fmin = 1e17), (this.fmax = -1e17);

    this.jitter = 0.5; //jitter added to points;
    this.jittab = [];

    this.rand.seed(0);
    for (let i = 0; i < 128; i++) {
      this.jittab.push(this.rand.random());
    }

    this.totpoint = 0;
  }

  remove_points(ps: number[], i1: number, i2: number, idxoff?: number) {
    idxoff = idxoff == undefined ? 0 : idxoff;
    const size = this.size;
    const fft = this.fft;

    for (let pi = i1; pi < i2; pi++) {
      const key = "" + (pi + idxoff);
      const px = this.fft_pointcachefx.get(key);
      const py = this.fft_pointcachefy.get(key);

      for (let i = 0; i < size * size; i++) {
        const ifx = i % size;
        const ify = ~~(i / size);
        const ret = this.calc_fxfy(px!, py!, ifx, ify);
        const fx = ret[0];
        const fy = ret[1];

        if (isNaN(fx) || isNaN(fy)) {
          throw new Error("nan " + fx + ", " + fy + ".");
        }
        fft[i * FTOT] -= fx;
        fft[i * FTOT + 1] -= fy;
      }
    }

    this.totpoint -= i2 - i1;
  }

  //if image is undefined, it will create and return one
  //image MUST match dimensions of fft
  raster(image?: ImageData): ImageData {
    const fft = this.fft;
    const size = this.size;
    const totpoint = this.totpoint;

    if (image == undefined) {
      image = new ImageData(this.size, this.size);
      image.data[0] = image.data[1] = image.data[2] = 0;
      image.data[3] = 255;

      const iview = new Int32Array(image.data.buffer);
      iview.fill(iview[0], 0, iview.length);
    }

    const idata = image.data;

    if (this.totpoint == 0) {
      report("Warning: no points in FFT");
      return image;
    }

    let min = 1e17;
    let max = -1e17;
    let average = 0.0;
    let mi = 0;

    for (var i = 0; i < fft.length; i += FTOT) {
      const fx = fft[i + FFX];
      const fy = fft[i + FFY];
      let p = fx * fx + fy * fy;
      p /= totpoint;

      average += p;
      mi++;

      min = Math.min(p, min);
      max = Math.max(p, max);
    }

    average /= mi;

    if (min == 1e17) {
      report("FFT error!");
      return image;
    }

    if (isNaN(min) || isNaN(max) || isNaN(average)) {
      console.log("min, max:", min, max);
      report("NaN!");
      throw new Error("nan");
    }

    if (min == max) {
      report("Yeek! Perfectly flat FFT! Bad data perhaps?");
      return image;
    }

    for (var i = 0; i < size * size; i++) {
      const x = i % size;
      const y = ~~(i / size);
      let idx = (y * size + x) * FTOT;
      const fx = fft[idx + FFX];
      const fy = fft[idx + FFY];
      let periodogram = fx * fx + fy * fy;
      periodogram /= totpoint;
      //periodogram = (periodogram - min) / (max - min);

      if (isNaN(fx) || isNaN(fy) || isNaN(periodogram)) {
        report("NaN!");
        throw new Error("nan!");
      }

      periodogram *= 0.5;

      const f = ~~(periodogram * 255);

      idx = (y * image.width + x) * 4;
      idata[idx] = idata[idx + 1] = idata[idx + 2] = ~~f;
      idata[idx + 3] = 255;
    }

    return image;
  }

  eval_radial(t: number): number | undefined {
    if (this.radial == undefined) return undefined;

    const ri = ~~(t * this.radial.average.length * 0.99999999);
    const tot = this.radial.totals[ri];
    const avg = this.radial.average;
    const totals = this.radial.totals;

    if (tot == 0) {
      let si = ri;
      while (si >= 0 && totals[si] == 0) {
        si--;
      }

      if (si < 0) return -1;

      let ei = ri;
      while (ei < totals.length && totals[ei] == 0) {
        ei++;
      }

      if (ei == totals.length) return avg[si] / totals[si];

      var t = (ri - si) / (ei - si);
      const a = avg[si] / totals[si];
      const b = avg[ei] / totals[ei];

      return a + (b - a) * (1.0 - t);
    }
    //return -1; //point does not exist in average

    return this.radial.average[ri] / tot;
  }

  calc_radial() {
    const totpoint = this.totpoint;

    if (totpoint == 0) return;

    const average = new Float64Array(~~(this.size / 7));
    const totals = new Float64Array(average.length);

    average.fill(0, 0, average.length);
    totals.fill(0, 0, totals.length);

    const fft = this.fft;
    const size = this.size;
    for (let si = 0; si < fft.length; si += FTOT) {
      let x = si % size;
      let y = ~~(si / size);

      x -= size * 0.5;
      y -= size * 0.5;

      let r = x * x + y * y;
      r = r != 0.0 ? Math.sqrt(r) : 0.0;

      const th = Math.atan2(y, x);

      r /= size;
      if (r > 1.0) {
        continue;
      }

      const fx = fft[si];
      const fy = fft[si + 1];
      const p = (fx * fx + fy * fy) / totpoint;
      const ri = ~~(r * average.length * 0.99999);

      average[ri] += p;
      totals[ri]++;
    }

    this.radial = {
      average: average,
      totals: totals,
    };
  }

  draw(g: CanvasRenderingContext2D, fftx: number, ffty: number, image: ImageData) {
    g.putImageData(image, fftx, ffty);

    const steps = 32;
    let t = 0;
    const dt = 1.0 / (steps - 1);
    const h = 40;
    const y = ffty + image.height + h + 2;

    g.strokeStyle = "black";
    g.beginPath();

    let first = 1;

    for (let i = 0; i < steps; i++, t += dt) {
      let f = this.eval_radial(t)!;

      //if (f < 0) continue; //fft wants us to skip this t value
      f = 1.0 - f;

      const x2 = fftx + i * 3;
      const y2 = y + f * h;
      //y2 = y;

      if (first) {
        g.moveTo(x2, y2);
        first = 0;
      } else {
        g.lineTo(x2, y2);
      }
    }

    g.stroke();
  }

  get(x: number, y: number): number {
    if (this.totpoint == 0) return 0;

    //x, y are in unit 0...<1 range

    //x = Math.min(Math.max(x, 0.0), 0.9999999999);
    //y = Math.min(Math.max(y, 0.0), 0.9999999999);

    x = ~~(x * this.size);
    y = ~~(y * this.size);

    const fft = this.fft;
    const idx = (y * this.size + x) * FTOT;
    const fx = fft[idx];
    const fy = fft[idx + 1];

    return (fx * fx + fy * fy) / this.totpoint;
  }

  calc_fxfy(x: number, y: number, ifx: number, ify: number): number[] {
    const ret = calc_fxfy_ret.next();
    const size = this.size;
    const fft = this.fft;
    //x += Math.random()/this.size;
    //y += Math.random()/this.size;

    const wx = ifx - size * 0.5;
    const wy = ify - size * 0.5;
    const th = -TWOPI * (wx * x + wy * y);

    if (isNaN(th)) {
      console.log(ifx, ify, x, y, wx, wy);
      throw new Error("NaN!");
    }

    ret[0] = _sin_table.cos(th);
    ret[1] = _sin_table.sin(th);

    return ret;
  }

  add_points(ps: number[], scale: number, i1: number, i2: number, idxoff?: number) {
    idxoff = idxoff == undefined ? 0 : idxoff;

    i1 *= PTOT;
    i2 *= PTOT;

    const size = this.size;
    const fft = this.fft;
    const fxcache = this.fft_pointcachefx;
    const fycache = this.fft_pointcachefy;
    const fmincache = this.fft_pointcachefy;
    const fmaxcache = this.fft_pointcachefy;
    const TWOPI = Math.PI * 2.0;
    this.totpoint += (i2 - i1) / PTOT;

    const jit = this.jitter;
    const jt = this.jittab;

    for (let i = i1; i < i2; i += PTOT) {
      let x = ps[i] * scale;
      let y = ps[i + 1] * scale;
      const pi = ~~(i / PTOT);
      //purpose of jitter to so we can handle
      //analyizing grid-aligned data, like void and cluster makes
      const j1 = (i / PTOT) % jt.length;
      const j2 = (j1 + i / PTOT) % jt.length;

      x += (jit * (jt[j1] - 0.5)) / scale / size;
      y += (jit * (jt[j2] - 0.5)) / scale / size;

      const key = "" + (pi + idxoff);
      fxcache.set(key, x);
      fycache.set(key, y);

      for (let si = 0; si < size * size; si++) {
        const ifx = si % size;
        const ify = ~~(si / size);
        let fx = 0;
        let fy = 0;
        const wx = ifx - size * 0.5;
        const wy = ify - size * 0.5;
        //x += Math.random()/this.size;
        //y += Math.random()/this.size;

        const th = -TWOPI * (wx * x + wy * y);
        const ret = this.calc_fxfy(x, y, ifx, ify);
        fx += ret[0];
        fy += ret[1];

        fft[si * FTOT + FFX] += fx;
        fft[si * FTOT + FFY] += fy;
      }
    }
  }
}
util.registerClass(FFT);

export { FFT };
